import { useRef } from "react";
import { Rocket, GraduationCap, Banknote, Users2, ArrowUpRight } from "lucide-react";
import { useReveal } from "@/lib/animations";

const JOIN_URL = "https://join.breezewealthpartners.com";

const PERKS = [
  {
    icon: Banknote,
    title: "Uncapped earning potential",
    text: "Top-tier compensation with no ceiling. Your effort decides your income — not a salary band.",
  },
  {
    icon: GraduationCap,
    title: "Mentorship that actually mentors",
    text: "Hands-on training from producers who've done it. We build leaders, not just license-holders.",
  },
  {
    icon: Users2,
    title: "Family-first culture",
    text: "Family first, business second. You'll grow in a team that celebrates your wins like their own.",
  },
  {
    icon: Rocket,
    title: "A real career path",
    text: "From new agent to agency owner — a proven roadmap with the leads, tech, and back-office support to get there.",
  },
];

export default function Careers() {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <main ref={ref} data-testid="careers-page" className="bg-[#F9F6F0]">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-36 md:pt-44 pb-20">
        <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
          Careers
        </p>
        <h1
          data-reveal
          className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#0B132B] max-w-4xl"
        >
          Build a career that{" "}
          <span className="text-gradient-breeze">builds families</span>.
        </h1>
        <p data-reveal className="mt-7 text-lg text-[#334155] font-light leading-relaxed max-w-2xl">
          We're growing — and we're looking for driven, coachable people who
          want meaningful work, real mentorship, and income with no ceiling. No
          insurance experience required.
        </p>
        <div data-reveal className="mt-10">
          <a
            href={JOIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="careers-learn-more-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] text-white font-semibold px-8 py-4 transition-all duration-300 hover:bg-[#29ABE2] hover:scale-105"
          >
            Learn More <ArrowUpRight size={17} />
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {PERKS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                data-reveal
                data-reveal-delay={i * 0.08}
                data-testid={`perk-card-${i}`}
                className="rounded-3xl bg-white border border-[#0B132B]/5 shadow-sm p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#29ABE2]/10 text-[#0a6fc2] flex items-center justify-center mb-5">
                  <Icon size={22} />
                </div>
                <h3 className="font-heading font-bold text-xl text-[#0B132B]">{p.title}</h3>
                <p className="mt-3 text-[#334155] font-light leading-relaxed text-sm">{p.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#0B132B] text-white grain relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 text-center">
          <h2
            data-reveal
            className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none max-w-2xl mx-auto"
          >
            Ready to write your own story?
          </h2>
          <p data-reveal className="mt-5 text-white/60 font-light max-w-md mx-auto">
            See what a career with Breeze Financial Group looks like — and take
            the first step today.
          </p>
          <div data-reveal className="mt-10">
            <a
              href={JOIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="careers-learn-more-button-bottom"
              className="inline-flex items-center gap-2 rounded-full bg-[#29ABE2] text-white font-semibold px-8 py-4 transition-all duration-300 hover:bg-white hover:text-[#0B132B] hover:scale-105"
            >
              Learn More <ArrowUpRight size={17} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
